import { useState, useCallback } from 'react';

/**
 * Custom hook to manage Health Analyzer integration
 * Handles health analysis state, results, and carrier mapping
 */
const useHealthAnalyzer = () => {
  const [underwritingClasses, setUnderwritingClasses] = useState([]);
  const [healthAnalyzerResults, setHealthAnalyzerResults] = useState({
    carrierValues: [],
    mappedCarrierIds: [],
    productIds: [],
    healthClasses: []
  });
  const [isHealthAnalyzerActive, setIsHealthAnalyzerActive] = useState(false);

  /**
   * Check if Health Analyzer has results
   */
  const hasHealthAnalyzerResults = useCallback(() => {
    return (
      (Array.isArray(underwritingClasses) && underwritingClasses.length > 0) ||
      (healthAnalyzerResults &&
        ((Array.isArray(healthAnalyzerResults.mappedCarrierIds) && healthAnalyzerResults.mappedCarrierIds.length > 0) ||
          (Array.isArray(healthAnalyzerResults.carrierValues) && healthAnalyzerResults.carrierValues.length > 0)))
    );
  }, [underwritingClasses, healthAnalyzerResults]);

  /**
   * Update Health Analyzer results
   */
  const updateHealthAnalyzerResults = useCallback(results => {
    setHealthAnalyzerResults(results);
    setIsHealthAnalyzerActive(true);
  }, []);

  /**
   * Update underwriting classes
   */
  const updateUnderwritingClasses = useCallback(classes => {
    setUnderwritingClasses(classes);
  }, []);

  /**
   * Clear Health Analyzer results
   */
  const clearHealthAnalyzerResults = useCallback(() => {
    setUnderwritingClasses([]);
    setHealthAnalyzerResults({
      carrierValues: [],
      mappedCarrierIds: [],
      productIds: [],
      healthClasses: []
    });
    setIsHealthAnalyzerActive(false);
  }, []);

  /**
   * Reset Health Analyzer to initial state
   */
  const resetHealthAnalyzer = useCallback(() => {
    clearHealthAnalyzerResults();
  }, [clearHealthAnalyzerResults]);

  /**
   * Get mapped carrier IDs from Health Analyzer results
   */
  const getMappedCarrierIds = useCallback(() => {
    return healthAnalyzerResults.mappedCarrierIds || [];
  }, [healthAnalyzerResults]);

  /**
   * Get health classes from Health Analyzer results
   */
  const getHealthClasses = useCallback(() => {
    return healthAnalyzerResults.healthClasses || [];
  }, [healthAnalyzerResults]);

  /**
   * Get product IDs from Health Analyzer results
   */
  const getProductIds = useCallback(() => {
    return healthAnalyzerResults.productIds || [];
  }, [healthAnalyzerResults]);

  /**
   * Apply Health Analyzer results to form data
   */
  const applyHealthAnalyzerToForm = useCallback(
    updateFormFields => {
      if (!hasHealthAnalyzerResults()) {
        return false;
      }

      const updates = {};

      // Update carriers if available
      if (healthAnalyzerResults.mappedCarrierIds && healthAnalyzerResults.mappedCarrierIds.length > 0) {
        updates.selectedCarriers = healthAnalyzerResults.mappedCarrierIds;
      }

      // Update health classes if available
      if (healthAnalyzerResults.healthClasses && healthAnalyzerResults.healthClasses.length > 0) {
        updates.healthClass = healthAnalyzerResults.healthClasses;
      }

      // Update product IDs if available
      if (healthAnalyzerResults.productIds && healthAnalyzerResults.productIds.length > 0) {
        updates.productIds = healthAnalyzerResults.productIds;
      }

      if (Object.keys(updates).length > 0 && updateFormFields) {
        updateFormFields(updates);
        return true;
      }

      return false;
    },
    [healthAnalyzerResults, hasHealthAnalyzerResults]
  );

  return {
    // State
    underwritingClasses,
    healthAnalyzerResults,
    isHealthAnalyzerActive,

    // Computed
    hasHealthAnalyzerResults: hasHealthAnalyzerResults(),

    // Getters
    getMappedCarrierIds,
    getHealthClasses,
    getProductIds,

    // Actions
    updateHealthAnalyzerResults,
    updateUnderwritingClasses,
    clearHealthAnalyzerResults,
    resetHealthAnalyzer,
    applyHealthAnalyzerToForm,

    // State setters for direct access
    setUnderwritingClasses,
    setHealthAnalyzerResults,
    setIsHealthAnalyzerActive
  };
};

export default useHealthAnalyzer;
